import { patientRegistryFetch } from "../api/patientRegistry";

/**
 * Shape of the patient-registry internal `/auth/identity` response.
 * Verified live 2026-05-09 against `partner-stage.xcures.com`.
 *
 * Note: `id`, `organizationId`, and `userId` are all UUIDs (strings).
 * `organization.name` and `projects[].name` are user-facing display values.
 */
export interface Identity {
    id: string;
    organizationId: string;
    organization: {
        id: string;
        name: string;
    };
    projects: Array<{
        id: string;
        name: string;
    }>;
}

export class IdentityShapeError extends Error {
    constructor(field: string) {
        super(`identity response missing or invalid: ${field}`);
        this.name = "IdentityShapeError";
    }
}

export class IdentityFetchError extends Error {
    public readonly status: number;
    constructor(status: number, body: string) {
        super(`identity fetch failed with status ${status}: ${body}`);
        this.name = "IdentityFetchError";
        this.status = status;
    }
}

/**
 * Fetches the user's identity record from the patient-registry.
 *
 * Per PRD-003 DoD:
 * - Bearer auth only; no `ProjectId` header. The fetch wrapper attaches
 *   `Authorization: Bearer <jwt>` automatically; we never pass a `headers`
 *   override here.
 * - 401s propagate as `AuthRequiredError` from the fetch wrapper, which has
 *   already cleared the JWT and reopened the paste modal.
 * - Shape errors throw `IdentityShapeError`; non-401 4xx and 5xx throw
 *   `IdentityFetchError`. Both are surfaced by `IdentityGate` as the error
 *   state with a Retry button.
 */
export async function fetchIdentity(): Promise<Identity> {
    const response = await patientRegistryFetch("/auth/identity");
    if (!response.ok) {
        const body = await response.text().catch(() => "(unreadable body)");
        throw new IdentityFetchError(response.status, body);
    }
    const data = (await response.json().catch(() => null)) as unknown;
    if (!isIdentity(data)) {
        throw new IdentityShapeError(describeMissingField(data));
    }
    return data;
}

function isIdentity(value: unknown): value is Identity {
    if (typeof value !== "object" || value === null) return false;
    const v = value as Record<string, unknown>;
    if (typeof v.id !== "string") return false;
    if (typeof v.organizationId !== "string") return false;
    const org = v.organization as Record<string, unknown> | undefined;
    if (!org || typeof org.id !== "string" || typeof org.name !== "string") return false;
    if (!Array.isArray(v.projects)) return false;
    for (const p of v.projects as unknown[]) {
        if (typeof p !== "object" || p === null) return false;
        const proj = p as Record<string, unknown>;
        if (typeof proj.id !== "string" || typeof proj.name !== "string") return false;
    }
    return true;
}

function describeMissingField(value: unknown): string {
    if (typeof value !== "object" || value === null) return "response is not an object";
    const v = value as Record<string, unknown>;
    if (typeof v.id !== "string") return "id";
    if (typeof v.organizationId !== "string") return "organizationId";
    const org = v.organization as Record<string, unknown> | undefined;
    if (!org) return "organization";
    if (typeof org.id !== "string") return "organization.id";
    if (typeof org.name !== "string") return "organization.name";
    if (!Array.isArray(v.projects)) return "projects";
    return "projects[].id or projects[].name";
}
